// Reusable status pill for order / stop / route rows. One component
// so the color mapping lives in exactly one place and every page in
// the app reads the same visual vocabulary:
//
//   gray   — inert / not-actionable (draft)
//   amber  — waiting for action (pending) — pairs with the count
//            strip's amber pending number so the badge and the
//            summary agree at a glance
//   blue   — in-progress (assigned, out_for_delivery, in_progress, arrived)
//   green  — successfully terminal (delivered, completed)
//   red    — unsuccessfully terminal or needs-attention (attempted,
//            failed, returned, cancelled, skipped)
//
// Unknown statuses fall back to gray with the raw value title-cased.
// Better to render a slightly-off badge than throw or hide; this also
// lets new enum members surface visibly during a deploy gap before
// the component is updated.
//
// Entity is accepted as a prop today only for future divergence
// (different label conventions, tooltips, icons). The current
// implementation uses entity only to pick disambiguation tones —
// e.g. route_status='assigned' and order_status='assigned' both map
// to blue and the same label. If a future entity status collides
// semantically with a different tone, we branch on entity here.

type Entity = 'order' | 'stop' | 'route';

type Tone = 'gray' | 'amber' | 'blue' | 'green' | 'red';

const TONE_CLASSES: Record<Tone, string> = {
  gray: 'border-gray-200 bg-gray-50 text-gray-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  blue: 'border-blue-200 bg-blue-50 text-blue-800',
  // emerald (not green-*) to match the existing success tone on Alert.tsx
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  red: 'border-red-200 bg-red-50 text-red-700',
};

// Tone per status. Keyed by lowercase status value across all three
// entity enums. Source enums (lynrx-prod, May 14, 2026):
//   order_status:  pending | assigned | out_for_delivery | delivered
//                | attempted | returned | cancelled | failed
//   route_status:  draft | assigned | in_progress | completed | cancelled
//   stop_status:   pending | arrived | completed | skipped | failed
const TONE_BY_STATUS: Record<string, Tone> = {
  // amber — waiting for action. Pending orders are the dispatcher's
  // hot pile; making the badge match the count strip color keeps the
  // "two pending" reading consistent at-a-glance.
  pending: 'amber',
  // gray — inert. Draft routes haven't been published; they're not
  // calling for anyone's attention yet.
  draft: 'gray',
  // blue — in motion
  assigned: 'blue',
  out_for_delivery: 'blue',
  in_progress: 'blue',
  arrived: 'blue',
  // 'published' kept for the May 6 design name (route_status used
  // 'published' originally; shipped enum uses 'assigned'). Cheap to
  // keep so badge doesn't visually regress if any code path still
  // emits the old string.
  published: 'blue',
  // green — happy-path terminal
  delivered: 'green',
  completed: 'green',
  // red — sad-path terminal / needs attention
  attempted: 'red',
  returned: 'red',
  cancelled: 'red',
  failed: 'red',
  skipped: 'red',
};

// Snake_case → Title Case ("out_for_delivery" → "Out for delivery").
// First word capitalized, rest left lowercase so the pill reads as a
// label, not as a constant.
function formatStatusLabel(status: string): string {
  const words = status.split('_');
  const first = words[0];
  if (!first) return status;
  const head = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return [head, ...words.slice(1).map((w) => w.toLowerCase())].join(' ');
}

export type StatusBadgeProps = {
  status: string;
  entity: Entity;
  className?: string;
};

export function StatusBadge({
  status,
  // entity intentionally accepted-but-unused today; see file header
  // for the why. Lint-silencing via underscore prefix would change
  // the public API, so we keep the name and add an eslint-disable
  // only if/when a linter actually complains.
  entity: _entity,
  className,
}: StatusBadgeProps): JSX.Element {
  const tone = TONE_BY_STATUS[status.toLowerCase()] ?? 'gray';
  const toneClass = TONE_CLASSES[tone];
  const base =
    'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap';
  return (
    <span
      className={className ? `${base} ${toneClass} ${className}` : `${base} ${toneClass}`}
      data-status={status}
      data-tone={tone}
    >
      {formatStatusLabel(status)}
    </span>
  );
}
